import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  BadgeDollarSign, 
  BarChart3, 
  Bell, 
  Calendar, 
  CreditCard, 
  DollarSign, 
  ShieldCheck, 
  UploadCloud 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { FeatureCarousel } from "@/components/FeatureCarousel";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/ui/logo";

const Hero = () => {
  const navigate = useNavigate();
  
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-24 md:py-40 max-w-5xl mx-auto relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>
      
      <div className="animate-fade-in">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
          <span className="gradient-text">Controle Total</span>
          <br />
          <span className="text-foreground">das Suas Finanças</span>
        </h1>
        
        <p className="mt-8 text-xl md:text-2xl text-muted-foreground max-w-3xl leading-relaxed">
          Transforme sua gestão financeira com nossa plataforma inteligente. 
          <span className="text-primary font-medium"> Organize, analise e prospere</span> com 
          ferramentas modernas e insights poderosos.
        </p>
        
        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button 
            size="lg" 
            onClick={() => navigate("/login")}
            className="h-14 px-8 text-lg font-semibold rounded-xl btn-primary group shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Começar Gratuitamente
            <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            className="h-14 px-8 text-lg font-medium rounded-xl border-2 hover:bg-accent/5 transition-all duration-300"
          >
            <BarChart3 className="mr-3 h-5 w-5" />
            Ver Demonstração
          </Button>
        </div>
        
        {/* Trust indicators */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-finance-green" />
            <span>100% Seguro</span>
          </div>
          <div className="flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-finance-blue" />
            <span>Backup Automático</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-finance-purple" />
            <span>Relatórios Inteligentes</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className="group relative flex flex-col items-center text-center p-8 rounded-2xl border bg-card card-hover overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    <div className="relative z-10">
      <div className="h-16 w-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10 text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-3 text-foreground">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </div>
  </div>
);

const Features = () => (
  <div className="py-24 px-6 bg-gradient-to-b from-background to-muted/20">
    <div className="text-center max-w-4xl mx-auto mb-20 animate-fade-in">
      <h2 className="text-4xl md:text-5xl font-bold mb-6">
        <span className="gradient-text">Funcionalidades</span> Principais
      </h2>
      <p className="text-xl text-muted-foreground leading-relaxed">
        Descubra todas as ferramentas que vão transformar sua gestão financeira. 
        Simplicidade e poder em uma única plataforma.
      </p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
      <div className="animate-slide-up" style={{animationDelay: '0.1s'}}>
        <FeatureCard
          icon={<CreditCard className="h-8 w-8" />}
          title="Registro Inteligente"
          description="Adicione transações rapidamente com preenchimento automático e categorização inteligente."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.2s'}}>
        <FeatureCard
          icon={<BarChart3 className="h-8 w-8" />}
          title="Dashboard Avançado"
          description="Visualize suas finanças com gráficos interativos e insights em tempo real."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.3s'}}>
        <FeatureCard
          icon={<Bell className="h-8 w-8" />}
          title="Alertas Inteligentes"
          description="Receba notificações personalizadas para nunca perder prazos importantes."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.4s'}}>
        <FeatureCard
          icon={<Calendar className="h-8 w-8" />}
          title="Calendário Financeiro"
          description="Organize e visualize todas suas obrigações em um calendário intuitivo."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.5s'}}>
        <FeatureCard
          icon={<BadgeDollarSign className="h-8 w-8" />}
          title="Relatórios Detalhados"
          description="Análises completas com insights que ajudam na tomada de decisões."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.6s'}}>
        <FeatureCard
          icon={<UploadCloud className="h-8 w-8" />}
          title="Backup na Nuvem"
          description="Seus dados sempre seguros e acessíveis em qualquer dispositivo."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.7s'}}>
        <FeatureCard
          icon={<ShieldCheck className="h-8 w-8" />}
          title="Segurança Total"
          description="Criptografia avançada e proteção de dados de nível bancário."
        />
      </div>
      <div className="animate-slide-up" style={{animationDelay: '0.8s'}}>
        <FeatureCard
          icon={<DollarSign className="h-8 w-8" />}
          title="IA Financeira"
          description="Insights automáticos e sugestões personalizadas para otimizar suas finanças."
        />
      </div>
    </div>
  </div>
);

const CallToAction = () => {
  const navigate = useNavigate();
  
  return (
    <div className="py-32 px-6 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-10 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-5xl mx-auto text-center relative z-10 animate-fade-in">
        <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
          Pronto para <span className="gradient-text">Transformar</span>
          <br />
          Suas Finanças?
        </h2>
        <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed">
          Junte-se a milhares de pessoas que já estão no controle total do seu dinheiro.
          <span className="text-primary font-medium"> Comece hoje mesmo</span> e veja a diferença.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
          <Button 
            size="lg" 
            onClick={() => navigate("/login")}
            className="h-16 px-10 text-xl font-semibold rounded-2xl btn-primary group shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            Criar Conta Gratuitamente
            <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            className="h-16 px-10 text-xl font-medium rounded-2xl border-2 hover:bg-accent/10 transition-all duration-300"
          >
            Falar com Especialista
          </Button>
        </div>
        
        {/* Social proof */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-muted-foreground">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent border-2 border-background"></div>
              ))}
            </div>
            <span className="text-sm font-medium">+5.000 usuários ativos</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex text-finance-amber">
              {[1,2,3,4,5].map(i => (
                <span key={i}>★</span>
              ))}
            </div>
            <span className="text-sm font-medium">4.9/5 avaliação média</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="bg-card py-12 px-6 border-t">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between">
        <div className="mb-8 md:mb-0">
          <Logo variant="full" size="md" />
          <p className="mt-4 text-muted-foreground max-w-xs">
            Sua solução completa para gestão financeira pessoal e empresarial.
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-semibold mb-4">Produto</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Funcionalidades</Link></li>
              <li><Link to="/pricing" className="text-muted-foreground hover:text-foreground">Preços</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">FAQ</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">Empresa</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Sobre Nós</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Contato</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Blog</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Carreiras</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Termos de Uso</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Privacidade</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground">Cookies</Link></li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="mt-12 pt-8 border-t border-border text-center text-muted-foreground text-sm">
        <p>© 2023 monelab. Todos os direitos reservados.</p>
      </div>
    </div>
  </footer>
);

const Header = () => {
  const navigate = useNavigate();
  
  return (
    <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50 animate-theme-transition">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Logo 
          variant="full" 
          size="md" 
          onClick={() => navigate("/")}
        />
        
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-200 relative group">
            Funcionalidades
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-200 relative group">
            Preços
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link to="/about" className="text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-200 relative group">
            Sobre
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-200 relative group">
            Contato
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
        </nav>
        
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/login")}
            className="font-medium hover:bg-accent/50 transition-all duration-200"
          >
            Entrar
          </Button>
          <Button 
            onClick={() => navigate("/login")}
            className="btn-primary font-medium px-6 rounded-xl"
          >
            Começar Grátis
          </Button>
        </div>
      </div>
    </header>
  );
};

export default function Index() {
  return (
    <div className="min-h-screen">
      <SEO
        title="monelab"
        description="Sistema inteligente de controle financeiro pessoal com relatórios avançados, categorização automática e insights baseados em IA para melhor gestão das suas finanças."
        keywords="controle financeiro, finanças pessoais, gestão financeira, orçamento, despesas, receitas, relatórios financeiros, IA financeira, aplicativo financeiro"
      />
      <Header />
      <main>
        <Hero />
        <FeatureCarousel />
        <Features />
        <CallToAction />
      </main>
      
      <Footer />
    </div>
  );
}
